import React from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Autocomplete,
  AutocompleteItem
} from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';
import { cdnQueryAtom, cdnListParamsAtom, cdnHydratedAtom, setDomainCdn } from '@/store';
import type { CdnItem } from '@/types';
import { toast } from 'react-toastify';

interface SetDnsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  domainId: number;
  domainName: string;
  onSuccess: () => void;
}

export function SetDnsModal({ isOpen, onOpenChange, domainId, domainName, onSuccess }: SetDnsModalProps) {
  const [urlOverride, setUrlOverride] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [selectedCdnId, setSelectedCdnId] = React.useState<string | null>(null);
  const [selectedCdnName, setSelectedCdnName] = React.useState<string>('');
  const [searchText, setSearchText] = React.useState('');

  // Use cdnQueryAtom with custom params for autocomplete
  const [params, setParams] = useAtom(cdnListParamsAtom);
  const [, setHydrated] = useAtom(cdnHydratedAtom);
  const [, setDomainCdnAtom] = useAtom(setDomainCdn);
  const { data: cdnsData, isLoading: isLoadingCdns } = useAtomValue(cdnQueryAtom);
  const cdns = cdnsData?.data || [];

  // Update params when search text changes (with debounce)
  React.useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      setParams((prev) => ({
        ...prev,
        page: 1,
        filters: searchText ? { name: searchText } : {}
      }));
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchText, isOpen, setParams]);

  // Reset params when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setHydrated(true); // Enable query for autocomplete
      setParams((prev) => ({
        ...prev,
        page: 1,
        filters: {}
      }));
      setSearchText('');
      setSelectedCdnId(null);
      setSelectedCdnName('');
    }
  }, [isOpen, setParams, setHydrated]);

  const handleSubmit = React.useCallback(async () => {
    if (!selectedCdnId && !urlOverride.trim()) return;

    try {
      setIsSubmitting(true);

      await setDomainCdnAtom({
        domainId,
        cdnId: selectedCdnId ? parseInt(selectedCdnId) : undefined,
        cdnUrlOverride: urlOverride.trim() || undefined
      });

      toast.success(`DNS settings updated for ${domainName}`);

      // Reset form
      setUrlOverride('');
      setSelectedCdnId(null);
      setSelectedCdnName('');
      setSearchText('');

      // Close modal and trigger refresh
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Failed to set DNS:', error);
      // Error is handled by axios interceptor
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedCdnId, urlOverride, domainId, domainName, onOpenChange, onSuccess, setDomainCdnAtom]);

  const hasCdnOrUrl = Boolean(selectedCdnId) || Boolean(urlOverride.trim());

  const handleKeyPress = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !isSubmitting && hasCdnOrUrl) {
        handleSubmit();
      }
    },
    [handleSubmit, isSubmitting, hasCdnOrUrl]
  );

  const handleClose = React.useCallback(() => {
    setUrlOverride('');
    setSelectedCdnId(null);
    setSelectedCdnName('');
    setSearchText('');
    onOpenChange(false);
  }, [onOpenChange]);

  const isFormValid = hasCdnOrUrl;

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="md" isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Set DNS for {domainName}</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4" onKeyDown={handleKeyPress}>
                {/* CDN Selection - Autocomplete */}
                <Autocomplete
                  label="CDN"
                  placeholder="Search and select CDN (optional if URL Override is set)..."
                  inputValue={selectedCdnId ? selectedCdnName : searchText}
                  isLoading={isLoadingCdns}
                  selectedKey={selectedCdnId}
                  isClearable
                  onInputChange={(value) => {
                    if (!selectedCdnId) {
                      setSearchText(value);
                    }
                  }}
                  onSelectionChange={(key) => {
                    if (key) {
                      const cdnId = key as string;
                      const cdn = cdns.find((c) => c.id.toString() === cdnId);
                      if (cdn) {
                        setSelectedCdnId(cdnId);
                        setSelectedCdnName(cdn.name);
                        setSearchText('');
                      }
                    }
                  }}
                  onClear={() => {
                    setSelectedCdnId(null);
                    setSelectedCdnName('');
                    setSearchText('');
                  }}
                  onOpenChange={() => {}} // Prevent modal from closing
                >
                  {cdns.map((cdn) => (
                    <AutocompleteItem key={cdn.id.toString()}>
                      <div className="flex flex-col">
                        <span className="font-medium">{cdn.name}</span>
                        <div className="text-default-500 flex items-center gap-2 text-xs">
                          <span>{cdn.cdn_tag}</span>
                          {cdn.provider?.name && (
                            <>
                              <span>•</span>
                              <span>{cdn.provider.name}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </AutocompleteItem>
                  ))}
                </Autocomplete>

                {/* URL Override */}
                <Input
                  label="URL Override"
                  placeholder="Custom URL or path (required if CDN not selected)"
                  value={urlOverride}
                  onValueChange={setUrlOverride}
                  description="One of CDN or URL Override is required. URL Override can be any address."
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose}>
                Cancel
              </Button>
              <Button color="primary" isLoading={isSubmitting} onPress={handleSubmit} isDisabled={!isFormValid}>
                Set DNS
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
